import type { AuditEventListResponse, SupportAccessStatus } from '@common/types/supportAccess';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAuditEvents, fetchSupportAccessStatus } from '@/services/SupportAccessService';

// A grant runs out on its own, so the status is read again every minute while it's on screen
const STATUS_REFETCH_INTERVAL_MS = 60_000;

function activeGrantKey(status: SupportAccessStatus | null | undefined) {
    return status ? `${status.isPrivate}:${status.activeGrant?.supportAccessGrantId}` : undefined;
}

export function useSupportAccessStatus(lawFirmId: number | undefined, enabled = true) {
    const queryClient = useQueryClient();
    return useQuery<SupportAccessStatus | null>({
        queryKey: ['supportAccess', lawFirmId],
        queryFn: async () => {
            if (!lawFirmId) return null;
            const previous = queryClient.getQueryData<SupportAccessStatus | null>([
                'supportAccess',
                lawFirmId,
            ]);
            const status = await fetchSupportAccessStatus(lawFirmId);
            // Lexo's access to the firm's client data started or ended (a grant, or the private
            // flag), so pages loaded before the change would show the wrong data
            if (previous && activeGrantKey(previous) !== activeGrantKey(status)) {
                void queryClient.invalidateQueries({
                    predicate: (query) => query.queryKey[0] !== 'supportAccess',
                });
            }
            return status;
        },
        enabled: enabled && !!lawFirmId,
        refetchInterval: STATUS_REFETCH_INTERVAL_MS,
    });
}

export function useAuditEvents(lawFirmId: number | undefined, page: number, pageSize: number) {
    return useQuery<AuditEventListResponse | null>({
        queryKey: ['supportAccess', lawFirmId, 'auditEvents', page, pageSize],
        queryFn: async () => {
            if (!lawFirmId) return null;
            return fetchAuditEvents(lawFirmId, page, pageSize);
        },
        enabled: !!lawFirmId,
        placeholderData: keepPreviousData,
    });
}
